import React, { useEffect, useState } from 'react';
import { apiUrl, apiUnreachableMessage } from '../config/api';

const CONTACT_MAX_TOTAL_BYTES = 25 * 1024 * 1024;
const ATTACHMENT_LIMIT_MESSAGE = 'Please keep attachments under 25 MBs only.';

function shortenFileName(name, maxLen = 36) {
  const text = String(name || '').trim();
  if (!text || text.length <= maxLen) return text;
  const lastDot = text.lastIndexOf('.');
  const ext = lastDot > 0 && lastDot < text.length - 1 ? text.slice(lastDot) : '';
  const base = ext ? text.slice(0, lastDot) : text;
  const budget = Math.max(6, maxLen - ext.length - 1);
  return `${base.slice(0, budget)}…${ext}`;
}

function Home() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    details: '',
  });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });

  useEffect(() => {
    return () => {
      files.forEach((item) => {
        if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      });
    };
    // Only revoke on unmount; removeAttachment handles individual cleanup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (status.message) setStatus({ type: '', message: '' });
  };

  const handleFileChange = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (!picked.length) return;
    const currentTotal = files.reduce((sum, item) => sum + (item.file?.size || 0), 0);
    const incomingTotal = picked.reduce((sum, file) => sum + (file?.size || 0), 0);
    if (currentTotal + incomingTotal > CONTACT_MAX_TOTAL_BYTES) {
      setStatus({ type: 'error', message: ATTACHMENT_LIMIT_MESSAGE });
      return;
    }
    const next = picked.map((file) => ({
      id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setFiles((prev) => [...prev, ...next]);
    if (status.message) setStatus({ type: '', message: '' });
  };

  const removeAttachment = (id) => {
    setFiles((prev) => {
      const target = prev.find((item) => item.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((item) => item.id !== id);
    });
  };

  const clearAttachments = () => {
    setFiles((prev) => {
      prev.forEach((item) => {
        if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      });
      return [];
    });
    const fileInput = document.getElementById('files');
    if (fileInput) fileInput.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: '', message: '' });
    const attachmentTotal = files.reduce((sum, item) => sum + (item.file?.size || 0), 0);
    if (attachmentTotal > CONTACT_MAX_TOTAL_BYTES) {
      setStatus({ type: 'error', message: ATTACHMENT_LIMIT_MESSAGE });
      return;
    }
    setSubmitting(true);
    try {
      const body = new FormData();
      body.append('name', formData.name.trim());
      body.append('email', formData.email.trim());
      body.append('phone', formData.phone.trim());
      body.append('address', formData.address.trim());
      body.append('details', formData.details.trim());
      files.forEach((item) => body.append('files', item.file));

      const res = await fetch(apiUrl('/api/contact'), {
        method: 'POST',
        body,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail =
          typeof data.detail === 'string'
            ? data.detail
            : Array.isArray(data.detail)
              ? data.detail.map((item) => item.msg || JSON.stringify(item)).join(' ')
              : 'Could not send your message. Please try again.';
        throw new Error(detail);
      }

      setFormData({ name: '', email: '', phone: '', address: '', details: '' });
      clearAttachments();
      setStatus({
        type: 'success',
        message:
          data.message ||
          'Thank you for your inquiry! We will contact you soon.',
      });
    } catch (err) {
      setStatus({
        type: 'error',
        message: err?.message || apiUnreachableMessage(),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const images = [
    { src: '/s2.jpg', alt: 'Site Safety 2' },
    { src: '/s3.jpg', alt: 'Site Safety 3' },
    { src: '/s4.jpg', alt: 'Site Safety 4' },
    { src: '/s5.jpg', alt: 'Site Safety 5' },
    { src: '/s6.jpg', alt: 'Site Safety 6' },
    { src: '/IMG_0945.jpg', alt: 'Site Safety 7' },
    { src: '/IMG_0969.jpg', alt: 'Site Safety 8' },
    { src: '/s9.jpg', alt: 'Site Safety 9' },
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section with Image */}
      <section className="relative flex min-h-[28rem] items-center overflow-hidden bg-gradient-to-r from-gray-900 to-black py-24 text-white md:min-h-[34rem] md:py-28">
        <div className="absolute inset-0 opacity-45">
          <img
            src="/IMG_0945.jpg"
            alt="Hero Background"
            className="h-full w-full object-cover"
            fetchPriority="high"
            decoding="async"
          />
        </div>
        <div className="absolute inset-0 bg-black/40" aria-hidden="true" />
        <div className="relative z-10 mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <h1 className="mb-6 text-4xl font-bold text-white md:text-5xl lg:text-6xl">
            Reliable Site Safety Solutions
          </h1>
          <p className="mb-8 text-xl text-gray-300 md:text-2xl">
            Expert safety services.
          </p>
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('contact')?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              });
            }}
            className="inline-block rounded-lg bg-[#eda448] px-8 py-3 font-semibold text-gray-900 shadow-lg transition-colors hover:bg-[#d8932e]"
          >
            Contact Us
          </a>
        </div>
      </section>

      {/* Image Gallery Section */}
      <section className="bg-black pb-10 pt-10 md:pb-12 md:pt-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-6 text-center">
            <h2 className="mb-3 text-3xl font-bold text-white md:text-4xl">
              Our Work in Action
            </h2>
            <div className="mx-auto h-1 w-24 bg-gray-600"></div>
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {images.map((image) => (
              <div
                key={image.src}
                className="group relative overflow-hidden rounded-lg border border-gray-800 transition-all hover:border-gray-600"
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  loading="lazy"
                  decoding="async"
                  className="h-64 w-full object-cover transition-transform duration-300 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 transition-all group-hover:bg-opacity-30"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About CSO Site Safety Section */}
      <section className="bg-black pb-10 pt-10 md:pb-12 md:pt-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-6 text-center">
            <h2 className="mb-3 text-3xl font-bold text-white md:text-4xl">
              About CSO Site Safety
            </h2>
            <div className="mx-auto h-1 w-24 bg-gray-600"></div>
          </div>

          <div className="mx-auto mb-0 grid max-w-5xl gap-12 md:grid-cols-2">
            <div>
              <div className="mb-6 rounded-lg overflow-hidden">
                <img 
                  src="/s3.jpg" 
                  alt="Our Mission" 
                  loading="lazy"
                  decoding="async"
                  className="w-full h-64 object-cover"
                />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-4">Our Mission</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                At CSO Site Safety Services Inc, we are dedicated to providing top-notch construction safety solutions. 
                Our mission is to ensure a safe working environment for all personnel on site, prioritizing safety 
                without compromising efficiency.
              </p>
            </div>
            <div>
              <div className="mb-6 rounded-lg overflow-hidden">
                <img 
                  src="/s4.jpg" 
                  alt="Our Services" 
                  loading="lazy"
                  decoding="async"
                  className="w-full h-64 object-cover"
                />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-4">Services</h3>
              <ul className="space-y-3 text-gray-300 leading-relaxed">
                <li>
                  <span className="font-semibold text-white">Fall Protection Training</span>
                  {' — '}Teaching workers how to work safely at heights and prevent falls
                </li>
                <li>
                  <span className="font-semibold text-white">WHMIS training</span>
                  {' — '}For working with chemicals or hazardous substances
                </li>
                <li>
                  <span className="font-semibold text-white">Mobile Elevating Work Platforms (MEWPs)</span>
                  {' — '}Scissor and boom lift certification
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Construction Safety Section with Images */}
      <section className="bg-black pb-10 pt-10 md:pb-12 md:pt-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-6 text-center">
            <h2 className="mb-3 text-3xl font-bold text-white md:text-4xl">
              Construction Safety, one project at a time
            </h2>
            <div className="mx-auto h-1 w-24 bg-gray-600"></div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="rounded-lg overflow-hidden border border-gray-800">
              <img 
                src="/s5.jpg" 
                alt="Safety Project 1" 
                loading="lazy"
                decoding="async"
                className="w-full h-64 object-cover"
              />
            </div>
            <div className="rounded-lg overflow-hidden border border-gray-800">
              <img 
                src="/s6.jpg" 
                alt="Safety Project 2" 
                loading="lazy"
                decoding="async"
                className="w-full h-64 object-cover"
              />
            </div>
            <div className="rounded-lg overflow-hidden border border-gray-800">
              <img 
                src="/IMG_0963.jpg" 
                alt="Safety Project 3" 
                loading="lazy"
                decoding="async"
                className="w-full h-64 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Us Section */}
      <section id="contact" className="bg-black pb-12 pt-10 md:pb-14 md:pt-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-6 text-center">
            <h2 className="mb-3 text-3xl font-bold text-white md:text-4xl">
              Contact Us
            </h2>
            <div className="mx-auto h-1 w-24 bg-gray-600"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Form */}
            <div className="bg-gray-900 rounded-lg shadow-lg p-8 border border-gray-800">
              <h3 className="text-2xl font-semibold text-white mb-6">Get a Free Quote!</h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    disabled={submitting}
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent disabled:opacity-60"
                    placeholder="Your Name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                    Email <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={submitting}
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent disabled:opacity-60"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    disabled={submitting}
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent disabled:opacity-60"
                    placeholder="(250) 540-8336"
                  />
                </div>

                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-gray-300 mb-2">
                    Address (Street, City, Zip Code)
                  </label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    disabled={submitting}
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent disabled:opacity-60"
                    placeholder="123 Main St, City, Zip Code"
                  />
                </div>

                <div>
                  <label htmlFor="details" className="block text-sm font-medium text-gray-300 mb-2">
                    Details
                  </label>
                  <textarea
                    id="details"
                    name="details"
                    value={formData.details}
                    onChange={handleChange}
                    rows={4}
                    disabled={submitting}
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent resize-none disabled:opacity-60"
                    placeholder="Let us know the details of what you are looking for, and we'll contact you with a quote."
                  />
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => document.getElementById('files').click()}
                      disabled={submitting}
                      className="flex items-center text-gray-300 hover:text-white transition-colors disabled:opacity-60"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                      <span className="underline">Attach Files</span>
                    </button>
                    <input
                      type="file"
                      id="files"
                      name="files"
                      multiple
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <span className="text-gray-400 text-sm">
                      Attachments ({files.length})
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">
                    Please keep attachments under 25 MBs only.
                  </p>

                  {files.length ? (
                    <ul className="space-y-0.5">
                      {files.map((item) => {
                        const isImage = String(item.file.type || '').startsWith('image/');
                        return (
                          <li
                            key={item.id}
                            className="flex min-w-0 items-center gap-1.5"
                          >
                            <div className="relative min-w-0 flex-1">
                              <a
                                href={item.previewUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group/file inline-block max-w-full truncate text-xs text-sky-300 underline decoration-sky-300/40 underline-offset-2 hover:text-sky-200"
                                title={item.file.name}
                              >
                                {shortenFileName(item.file.name, 48)}
                                {isImage ? (
                                  <span className="pointer-events-none absolute left-0 top-full z-20 mt-1 hidden w-36 rounded border border-gray-700 bg-gray-950 p-1 shadow-lg group-hover/file:block">
                                    <img
                                      src={item.previewUrl}
                                      alt=""
                                      className="h-20 w-full rounded object-contain"
                                    />
                                  </span>
                                ) : null}
                              </a>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeAttachment(item.id)}
                              disabled={submitting}
                              className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-sm leading-none text-red-400 hover:text-red-300 disabled:opacity-60"
                              aria-label={`Remove ${item.file.name}`}
                              title="Remove"
                            >
                              ×
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </div>

                {status.message ? (
                  <div
                    className={`rounded-lg px-3 py-2 text-sm ${
                      status.type === 'success'
                        ? 'bg-green-400/10 text-green-400'
                        : 'bg-red-400/10 text-red-400'
                    }`}
                  >
                    {status.message}
                  </div>
                ) : null}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full text-gray-900 font-semibold py-3 px-6 rounded bg-[#eda448] hover:bg-[#d8932e] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Sending…' : 'SEND'}
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div className="bg-gray-900 rounded-lg shadow-lg p-8 border border-gray-800">
                <h3 className="text-2xl font-semibold text-white mb-4">
                  Better yet, see us in person!
                </h3>
                <p className="text-gray-300 leading-relaxed mb-6">
                  We stay in constant communication with our customers until the job is done. 
                  To get a free quote, or if you have questions or special requests, just drop us a line.
                </p>
                <div className="mt-6 rounded-lg overflow-hidden border border-gray-800">
                  <img 
                    src="/s8.jpg" 
                    alt="Our Location" 
                    loading="lazy"
                    decoding="async"
                    className="w-full h-48 object-cover"
                  />
                </div>
              </div>

              <div className="bg-gray-900 rounded-lg shadow-lg p-8 border border-gray-800">
                <h3 className="text-xl font-semibold text-white mb-4">
                  CSO Site Safety Services Inc
                </h3>
                <p className="text-gray-300 mb-2">Salmon Arm, BC, Canada</p>
                <p className="text-gray-300">
                  <a href="tel:250-540-8336" className="text-white hover:text-gray-300">
                    250-540-8336
                  </a>
                </p>
                <div className="mt-6 rounded-lg overflow-hidden border border-gray-800">
                  <img 
                    src="/s9.jpg" 
                    alt="Contact Us" 
                    loading="lazy"
                    decoding="async"
                    className="w-full h-48 object-cover"
                  />
                </div>
              </div>

              <div className="bg-gray-900 rounded-lg shadow-lg p-8 border border-gray-800">
                <h3 className="text-xl font-semibold text-white mb-4">Hours</h3>
                <table className="w-full text-left">
                  <tbody>
                    <tr className="border-b border-gray-800">
                      <td className="py-2 pr-4 text-gray-300 font-medium">Monday – Friday</td>
                      <td className="py-2 text-gray-300">07:00 a.m. – 05:00 p.m.</td>
                    </tr>
                    <tr className="border-b border-gray-800">
                      <td className="py-2 pr-4 text-gray-300 font-medium">Saturday</td>
                      <td className="py-2 text-gray-300">By appointment</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4 text-gray-300 font-medium">Sunday</td>
                      <td className="py-2 text-gray-300">Closed</td>
                    </tr>
                  </tbody>
                </table>
                <p className="mt-4 text-sm text-gray-400">
                  Site support available during active project hours. Emergency after-hours by arrangement.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
